// components/admin/LoginLogsScreen.js
import React, { useState, useEffect } from 'react';

const LoginLogsScreen = ({ user, token, onBack, isDarkMode, api }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    username: '',
    success: '',
    days: 30
  });
  const [loading, setLoading] = useState(false);

  const themeStyles = {
    background: isDarkMode ? '#1a1a1a' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000',
    inputBg: isDarkMode ? '#2d2d2d' : '#ffffff',
    inputBorder: isDarkMode ? '#404040' : '#d1d5db',
    buttonBg: isDarkMode ? '#374151' : '#e5e7eb',
    buttonBorder: isDarkMode ? '#4b5563' : '#d1d5db',
    tableHeaderBg: isDarkMode ? '#2d2d2d' : '#f2f2f2',
    tableBorder: isDarkMode ? '#404040' : '#000000',
    cardBg: isDarkMode ? '#2d2d2d' : '#f9fafb',
  };

  // Load logs and stats
  useEffect(() => {
    loadLogs();
    loadStats();
  }, [currentPage, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 25,
        ...filters
      });
      
      const response = await api.get(`/admin/login-logs?${params}`, token);
      setLogs(response.logs);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = new URLSearchParams({ days: filters.days });
      const response = await api.get(`/admin/login-stats?${params}`, token);
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (success) => {
    if (success) {
      return (
        <span style={{
          padding: '2px 8px',
          backgroundColor: '#bbf7d0',
          color: '#166534',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          Success
        </span>
      );
    } else {
      return (
        <span style={{
          padding: '2px 8px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          Failed
        </span>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: themeStyles.background,
      color: themeStyles.color,
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Login Logs</h1>
        <button
          onClick={onBack}
          style={{
            padding: '8px 16px',
            backgroundColor: themeStyles.buttonBg,
            color: themeStyles.color,
            border: `1px solid ${themeStyles.buttonBorder}`,
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to User Management
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{ backgroundColor: themeStyles.cardBg, padding: '20px', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Total Attempts</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>{stats.overall.total_attempts}</p>
          </div>
          <div style={{ backgroundColor: themeStyles.cardBg, padding: '20px', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Successful Logins</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>{stats.overall.successful_logins}</p>
          </div>
          <div style={{ backgroundColor: themeStyles.cardBg, padding: '20px', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Failed Attempts</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>{stats.overall.failed_attempts}</p>
          </div>
          <div style={{ backgroundColor: themeStyles.cardBg, padding: '20px', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Unique Users</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>{stats.overall.unique_users}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: themeStyles.cardBg,
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        alignItems: 'end'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Username:</label>
          <input
            type="text"
            value={filters.username}
            onChange={(e) => handleFilterChange('username', e.target.value)}
            placeholder="Filter by username"
            style={{
              padding: '8px',
              border: `1px solid ${themeStyles.inputBorder}`,
              borderRadius: '4px',
              backgroundColor: themeStyles.inputBg,
              color: themeStyles.color,
              width: '150px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Status:</label>
          <select
            value={filters.success}
            onChange={(e) => handleFilterChange('success', e.target.value)}
            style={{
              padding: '8px',
              border: `1px solid ${themeStyles.inputBorder}`,
              borderRadius: '4px',
              backgroundColor: themeStyles.inputBg,
              color: themeStyles.color,
              width: '120px'
            }}
          >
            <option value="">All</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Time Period:</label>
          <select
            value={filters.days}
            onChange={(e) => handleFilterChange('days', e.target.value)}
            style={{
              padding: '8px',
              border: `1px solid ${themeStyles.inputBorder}`,
              borderRadius: '4px',
              backgroundColor: themeStyles.inputBg,
              color: themeStyles.color,
              width: '120px'
            }}
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Login Logs Table */}
      <div style={{ backgroundColor: themeStyles.cardBg, borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading login logs...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{
                  border: `1px solid ${themeStyles.tableBorder}`,
                  padding: '12px',
                  backgroundColor: themeStyles.tableHeaderBg,
                  color: themeStyles.color,
                  textAlign: 'left'
                }}>
                  Date/Time
                </th>
                <th style={{
                  border: `1px solid ${themeStyles.tableBorder}`,
                  padding: '12px',
                  backgroundColor: themeStyles.tableHeaderBg,
                  color: themeStyles.color,
                  textAlign: 'left'
                }}>
                  Username
                </th>
                <th style={{
                  border: `1px solid ${themeStyles.tableBorder}`,
                  padding: '12px',
                  backgroundColor: themeStyles.tableHeaderBg,
                  color: themeStyles.color,
                  textAlign: 'left'
                }}>
                  Status
                </th>
                <th style={{
                  border: `1px solid ${themeStyles.tableBorder}`,
                  padding: '12px',
                  backgroundColor: themeStyles.tableHeaderBg,
                  color: themeStyles.color,
                  textAlign: 'left'
                }}>
                  Failure Reason
                </th>
                <th style={{
                  border: `1px solid ${themeStyles.tableBorder}`,
                  padding: '12px',
                  backgroundColor: themeStyles.tableHeaderBg,
                  color: themeStyles.color,
                  textAlign: 'left'
                }}>
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={index}>
                  <td style={{
                    border: `1px solid ${themeStyles.tableBorder}`,
                    padding: '12px',
                    color: themeStyles.color
                  }}>
                    {formatDateTime(log.attempted_at)}
                  </td>
                  <td style={{
                    border: `1px solid ${themeStyles.tableBorder}`,
                    padding: '12px',
                    color: themeStyles.color
                  }}>
                    {log.username}
                  </td>
                  <td style={{
                    border: `1px solid ${themeStyles.tableBorder}`,
                    padding: '12px',
                    color: themeStyles.color
                  }}>
                    {getStatusBadge(log.success)}
                  </td>
                  <td style={{
                    border: `1px solid ${themeStyles.tableBorder}`,
                    padding: '12px',
                    color: themeStyles.color
                  }}>
                    {log.failure_reason || '-'}
                  </td>
                  <td style={{
                    border: `1px solid ${themeStyles.tableBorder}`,
                    padding: '12px',
                    color: themeStyles.color,
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    {log.ip_address || 'Unknown'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPage === 1 ? themeStyles.inputBg : themeStyles.buttonBg,
                color: themeStyles.color,
                border: `1px solid ${themeStyles.buttonBorder}`,
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1
              }}
            >
              Previous
            </button>
            
            <span style={{ padding: '8px 12px', color: themeStyles.color }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPage === totalPages ? themeStyles.inputBg : themeStyles.buttonBg,
                color: themeStyles.color,
                border: `1px solid ${themeStyles.buttonBorder}`,
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginLogsScreen;